// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { useLedgerSync } from '../../hooks/useLedgerSync.js';
import { formatAmount, LIABILITY_TYPES } from '../../lib/eventLog.js';
import { MobileTopBar } from './MobileTopBar.js';
import { PullToRefresh } from './PullToRefresh.js';
import { ChevronRightIcon, ChevronDownIcon, SearchIcon, CloseIcon } from './icons.js';
import type { Account, AccountType } from '../../types.js';

interface MobileAccountTreeProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

type GroupLabelKey =
  | 'accountTypeGroups.assets'
  | 'accountTypeGroups.liabilities'
  | 'accountTypeGroups.income'
  | 'accountTypeGroups.expenses'
  | 'accountTypeGroups.equity'
  | 'accountTypeGroups.trading';

const TOP_LEVEL_GROUPS: { labelKey: GroupLabelKey; types: AccountType[] }[] = [
  { labelKey: 'accountTypeGroups.assets', types: ['ASSET', 'BANK', 'CASH', 'INVESTMENT', 'RECEIVABLE', 'MUTUAL_FUND', 'STOCK', 'ACCOUNTS_RECEIVABLE'] },
  { labelKey: 'accountTypeGroups.liabilities', types: ['LIABILITY', 'CREDIT', 'PAYABLE', 'CREDIT_CARD', 'ACCOUNTS_PAYABLE'] },
  { labelKey: 'accountTypeGroups.income', types: ['INCOME'] },
  { labelKey: 'accountTypeGroups.expenses', types: ['EXPENSE'] },
  { labelKey: 'accountTypeGroups.equity', types: ['EQUITY'] },
  { labelKey: 'accountTypeGroups.trading', types: ['TRADING'] },
];

function balanceColor(account: Account, subtotal: number): string {
  if (account.placeholder) return 'text-text-subtle';
  if (LIABILITY_TYPES.has(account.type)) {
    return subtotal > 0 ? 'text-positive' : subtotal < 0 ? 'text-negative' : 'text-text-muted';
  }
  return subtotal < 0 ? 'text-negative' : 'text-text';
}

export function MobileAccountTree({ worker, keyData }: MobileAccountTreeProps) {
  const { t } = useTranslation(['mobile', 'accounting', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const { sync } = useLedgerSync(worker, keyData);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState<string | null>(null); // null = search hidden
  const accounts = useMemo(() => [...ledger.accounts.values()], [ledger.accounts]);
  const rootIds = useMemo(
    () => new Set(accounts.filter((a) => a.type === 'ROOT').map((a) => a.id)),
    [accounts],
  );

  const subtotalOf = useMemo(() => {
    const byParent = new Map<string, Account[]>();
    for (const a of accounts) {
      const key = a.parentId ?? '';
      const arr = byParent.get(key) ?? [];
      arr.push(a);
      byParent.set(key, arr);
    }
    const cache = new Map<string, number>();
    function compute(a: Account): number {
      const cached = cache.get(a.id);
      if (cached !== undefined) return cached;
      let total = a.balance;
      for (const child of byParent.get(a.id) ?? []) total += compute(child);
      cache.set(a.id, total);
      return total;
    }
    return compute;
  }, [accounts]);

  const q = (search ?? '').trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return null;
    return accounts.filter(
      (a) => a.type !== 'ROOT' && (a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)),
    );
  }, [accounts, q]);

  return (
    <>
      <MobileTopBar
        title={t('common:nav.accounts')}
        right={
          <button
            onClick={() => setSearch((s) => (s === null ? '' : null))}
            aria-label={t('accountPicker.searchAccounts')}
            className="w-10 h-10 flex items-center justify-center text-text-muted active:text-text"
          >
            {search === null ? <SearchIcon size={20} /> : <CloseIcon size={20} />}
          </button>
        }
      />

      {search !== null && (
        <div className="shrink-0 bg-bg-surface border-b border-border px-3 py-2">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('accountPicker.searchAccountsPlaceholder')}
            className="w-full bg-bg border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none placeholder-text-subtle"
          />
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto native-scroll pb-tabbar">
        <PullToRefresh onRefresh={sync} scrollRef={scrollRef} />

        {matches ? (
          matches.length === 0 ? (
            <p className="text-center text-text-muted text-sm py-16">{t('accountPicker.noMatching')}</p>
          ) : (
            <ul>
              {matches.map((a) => (
                <li key={a.id}>
                  <FlatRow account={a} subtotal={subtotalOf(a)} onOpen={() => navigate(`/accounts/${a.id}`)} />
                </li>
              ))}
            </ul>
          )
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center px-8 py-20">
            <p className="text-md font-medium text-text mb-1">{t('accounting:accountTree.noAccounts')}</p>
            <p className="text-sm text-text-muted mb-5">{t('accounting:accountTree.createFirstPrompt')}</p>
            <button
              onClick={() => navigate('/setup')}
              className="px-5 h-11 flex items-center bg-accent text-white text-sm font-medium active:bg-accent-hover"
            >
              {t('accounting:accountTree.setupStarter')}
            </button>
          </div>
        ) : (
          TOP_LEVEL_GROUPS.map(({ labelKey, types }) => {
            const groupAccounts = accounts.filter(
              (a) => types.includes(a.type) && (a.parentId === null || rootIds.has(a.parentId)) && !a.hidden,
            );
            if (groupAccounts.length === 0) return null;
            return (
              <section key={labelKey}>
                <h2 className="px-4 pt-4 pb-1 text-xs font-medium text-text-subtle uppercase tracking-widest">
                  {t(`accounting:${labelKey}`)}
                </h2>
                {groupAccounts.map((a) => (
                  <TreeRow
                    key={a.id}
                    account={a}
                    accounts={accounts}
                    depth={0}
                    subtotalOf={subtotalOf}
                    onOpen={(id) => navigate(`/accounts/${id}`)}
                  />
                ))}
              </section>
            );
          })
        )}
      </div>
    </>
  );
}

// A tree row: the chevron toggles expansion (its own 44px target); tapping the
// rest of the row opens the register. This resolves the desktop ambiguity where
// clicking a group both expanded AND navigated (Phase 5 requirement).
function TreeRow({
  account,
  accounts,
  depth,
  subtotalOf,
  onOpen,
}: {
  account: Account;
  accounts: Account[];
  depth: number;
  subtotalOf: (a: Account) => number;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation(['mobile']);
  const [expanded, setExpanded] = useState(depth === 0);
  const children = accounts.filter((a) => a.parentId === account.id && !a.hidden);
  const subtotal = subtotalOf(account);

  return (
    <>
      <div
        className="touch-row flex items-center border-b border-border active:bg-bg-elevated"
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        {children.length > 0 ? (
          <button
            onClick={() => setExpanded((x) => !x)}
            aria-label={expanded ? t('accountTree.collapse') : t('accountTree.expand')}
            className="w-11 h-11 flex items-center justify-center text-text-muted shrink-0"
          >
            {expanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          </button>
        ) : (
          <span className="w-11 shrink-0" />
        )}
        <button
          onClick={() => !account.placeholder && onOpen(account.id)}
          disabled={account.placeholder}
          className="flex-1 flex items-center justify-between gap-3 pr-4 h-11 text-left disabled:cursor-default"
        >
          <span className={`text-md truncate ${account.placeholder ? 'text-text-subtle' : 'text-text'}`}>
            {account.name}
          </span>
          <span className={`text-sm font-mono tabular-nums shrink-0 ${balanceColor(account, subtotal)}`}>
            {formatAmount(subtotal, account.currency)}
          </span>
        </button>
      </div>
      {expanded && children.map((c) => (
        <TreeRow key={c.id} account={c} accounts={accounts} depth={depth + 1} subtotalOf={subtotalOf} onOpen={onOpen} />
      ))}
    </>
  );
}

function FlatRow({ account, subtotal, onOpen }: { account: Account; subtotal: number; onOpen: () => void }) {
  return (
    <button
      onClick={() => !account.placeholder && onOpen()}
      disabled={account.placeholder}
      className="w-full touch-row flex items-center justify-between gap-3 px-4 h-12 border-b border-border text-left active:bg-bg-elevated disabled:cursor-default"
    >
      <span className={`text-md truncate ${account.placeholder ? 'text-text-subtle' : 'text-text'}`}>
        {account.name}
      </span>
      <span className={`text-sm font-mono tabular-nums shrink-0 ${balanceColor(account, subtotal)}`}>
        {formatAmount(subtotal, account.currency)}
      </span>
    </button>
  );
}
